import React from 'react';

import { ColorPicker, ValueGroup } from '@candu-ai/craft/ui';
import { Flags, useFlags } from 'commons/hooks';
import Section from 'craft/containers/Toolbox/components/Section';
import { Border } from 'craft/containers/Toolbox/sections/Border';
import {
  StyleHierarchy,
  getCssResetStyleMessage,
  useCss,
  useEditor,
  useMultiNodes,
} from 'craft/hooks';
import { CssSelectorId } from 'craft/types';

const BackdropDefaultValues = {
  borderColor: {
    fallbackStyle: StyleHierarchy.Styleguide,
    fallbackValue: '#413EDF',
  },
  borderWidth: {
    fallbackStyle: StyleHierarchy.Styleguide,
    fallbackValue: '2px',
  },
  borderRadius: {
    fallbackStyle: StyleHierarchy.Styleguide,
    fallbackValue: '0',
  },
};

export const TourBackdrop = ({ nodeIds }: { nodeIds: string[] }) => {
  const { query } = useEditor();
  const { [Flags.TOUR_STEP_BACKDROP]: tourStepBackdrop } = useFlags();
  const { setCss: setContainerCss } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.TourStepBackdropContainer,
  });
  const {
    css: wrapperCss,
    setCss: setWrapperCss,
    stylesHierarchy: wrapperStylesHierarchy,
  } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.TourStepBackdropWrapper,
  });

  const {
    props: { hasBackdrop },
    actions: { setProps },
  } = useMultiNodes(nodeIds);
  const isBackdropEnabled = hasBackdrop?.value;

  const siblingNodes = query.node(nodeIds[0])?.getParent()?.childNodes();
  let firstNodeWithBackdrop = {} as any;
  siblingNodes?.forEach((nodeId) => {
    const node = query.node(nodeId)?.get();
    if (node?.data?.props.hasBackdrop) {
      firstNodeWithBackdrop = node?.data;
    }
  });

  const handleToggleBackdrop = () => {
    if (!isBackdropEnabled && firstNodeWithBackdrop?.custom?.cssSelectors) {
      const cssSelectors = firstNodeWithBackdrop?.custom?.cssSelectors || {};
      const containerCss = cssSelectors[CssSelectorId.TourStepBackdropContainer]?.css;
      const newWrapperCss = cssSelectors[CssSelectorId.TourStepBackdropWrapper]?.css;

      setContainerCss(containerCss);
      setWrapperCss(newWrapperCss);
    }

    setProps({ hasBackdrop: !isBackdropEnabled });
  };

  if (!tourStepBackdrop) {
    return null;
  }

  return (
    <Section name="Backdrop" isExpanded={isBackdropEnabled} onToggle={handleToggleBackdrop}>
      <ValueGroup>
        <ColorPicker
          label="Overlay Color"
          isInGroup
          onResetTooltip={getCssResetStyleMessage(StyleHierarchy.Styleguide)}
          defaultValue={
            (wrapperStylesHierarchy?.backgroundColor?.fallbackValue as string) || '#D9D9D9EE'
          }
          value={wrapperCss.backgroundColor}
          onChange={(newColor) => {
            setWrapperCss({ backgroundColor: newColor });
          }}
        />
        <Border
          nodeIds={nodeIds}
          hasOverflow={false}
          hasSectionWrapper={false}
          defaultValues={BackdropDefaultValues}
          schema={{
            isVisible: true,
            cssSelectorId: CssSelectorId.TourStepBackdropContainer,
          }}
        />
      </ValueGroup>
    </Section>
  );
};
